import { BasicPlanPricing } from "../../../ChoosePlanCarePlus/pricing";
import { CARE_PLUS_ORANGE_GRADIENT } from "../../../CarePlusShared/carePlusUi";
import checkIcon from "../../../../icons/wl-reorder-check.svg";
import dividerLine from "../../../../icons/wl-reorder-divider.svg";
import * as translator from "../../../../utils/translator";

const BENEFIT_KEYS = ["benefit_track_progress", "benefit_goal_weight", "benefit_cancel_anytime", "benefit_monthly_delivery"];

interface Props {
  language: string;
  country: string;
  currencyRate?: number;
  pricing: BasicPlanPricing;
  medicationPrice: number;
  /** Wegovy pill: services row + second-month note; others show the dosage disclaimer. */
  isTabletProduct: boolean;
  /** Only when the one-time option is shown. */
  selectable: boolean;
  isSelected: boolean;
  onSelect: () => void;
}

const OrangeCheck = () => (
  <span className="flex size-[16px] shrink-0 items-center justify-center rounded-full drop-shadow-[0px_3.2px_4px_rgba(245,147,1,0.3)]">
    <img src={checkIcon} alt="" width="9.6" height="9.6" />
  </span>
);

const PriceRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="flex w-full items-center justify-between py-[5px] font-roboto text-[14px] leading-[20px]">
    <span className="text-[#636363]">{label}</span>
    <span>{children}</span>
  </div>
);

/** Figma "CarePlusCard". */
const ReorderCarePlusPlanCard = ({
  language,
  country,
  currencyRate = 1,
  pricing,
  medicationPrice,
  isTabletProduct,
  selectable,
  isSelected,
  onSelect,
}: Props) => {
  // English when the language has no copy yet (.replace below must never get null).
  const text = (key: string): string =>
    translator.translate(language, "reorder", `WlReorder/${key}`) ?? translator.translate("en", "reorder", `WlReorder/${key}`) ?? "";
  const price = (amount: number) => translator.getPrice(amount, `${language.toLowerCase()}-${country.toUpperCase()}`, currencyRate);
  const footnote = isTabletProduct
    ? text("second_month_note").replace("::medication", price(medicationPrice)).replace("::services", price(pricing.secondMonthFee))
    : text("price_disclaimer");

  return (
    <div
      data-testid="wl-reorder-care-plus-card"
      role={selectable ? "radio" : undefined}
      aria-checked={selectable ? isSelected : undefined}
      onClick={onSelect}
      className={`flex w-full flex-col overflow-hidden rounded-[16px] border-2 border-solid bg-white ${selectable ? "cursor-pointer" : ""} ${
        isSelected ? "border-[#f59603]" : "border-[#e2e8df]"
      }`}
    >
      <div className="flex w-full items-center justify-between bg-[#f59603] px-[16px] py-[8px]">
        <span className="font-poppins text-[12px] font-bold uppercase leading-[16.5px] tracking-[1.1px] text-white">{text("recommended")}</span>
        <span className="font-roboto text-[12px] font-semibold leading-[16.5px] text-[rgba(255,255,255,0.8)]">{text("most_popular")}</span>
      </div>

      <div className="flex w-full flex-col gap-[22px] p-[16px]">
        <div className="flex flex-col">
          <div className="flex items-baseline gap-[8px] whitespace-nowrap">
            <span className="font-poppins text-[16px] font-semibold leading-[24px] text-[#08080a]">{text("monthly_plan")}</span>
            <span className="font-serif-display text-[23px] italic leading-[19px] text-[#f59603]">Care+</span>
          </div>
          <span className="pt-[2px] font-roboto text-[12px] leading-[15px] text-[#616975]">{text("plan_subtitle")}</span>
        </div>

        <div className="flex w-full flex-col gap-[8px]">
          {pricing.savings > 0 && (
            <div className="flex w-full items-center">
              <span
                data-testid="wl-reorder-care-plus-savings"
                className="rounded-full px-[16px] py-[6px] font-poppins text-[12px] font-semibold leading-[16.5px] text-white"
                style={{ backgroundImage: CARE_PLUS_ORANGE_GRADIENT }}
              >
                {text("save_this_month").replace("::amount", price(pricing.savings))}
              </span>
            </div>
          )}

          <ul className="m-0 flex w-full list-none flex-col gap-[8px] p-0">
            {BENEFIT_KEYS.map((key) => (
              <li key={key} className="flex min-h-[30px] w-full items-center gap-[4px]">
                <OrangeCheck />
                <span className="min-w-0 flex-1 font-poppins text-[14px] leading-[20px] text-[#08080a]">{text(key)}</span>
              </li>
            ))}
          </ul>

          <div className="relative h-0 w-full">
            <div className="absolute inset-x-0 top-[-0.5px] h-px">
              <img src={dividerLine} alt="" width="325" height="1" className="block h-full w-full" />
            </div>
          </div>

          <div className="flex w-full flex-col">
            <PriceRow label={text("medication")}>
              <span className="font-semibold text-[#08080a]">{price(medicationPrice)}</span>
              <span className="text-[#616975]">{text("per_month")}</span>
            </PriceRow>
            {isTabletProduct && (
              <PriceRow label={text("care_plus_services")}>
                <span data-testid="wl-reorder-care-plus-services" className="text-[#61a333]">
                  <span className="font-semibold">
                    {pricing.chargedFee === 0 ? price(0).replace(/[.,]0+/, "") : price(pricing.chargedFee)}
                  </span>
                  {text("first_month")}
                </span>
              </PriceRow>
            )}
          </div>

          <div className="flex w-full flex-col items-end justify-center">
            <div className="flex w-full items-start justify-between font-poppins text-[20px] font-medium leading-[25px] text-[#08080a]">
              <span>{text("total")}</span>
              <span data-testid="wl-reorder-care-plus-total">{price(pricing.headerCharged)}</span>
            </div>
            {pricing.hasDiscount && (
              <span data-testid="wl-reorder-care-plus-usually" className="pt-[4px] font-inter text-[14px] leading-[16px] text-[#616975] line-through">
                {text("usually")} {price(pricing.headerTotal)}
              </span>
            )}
          </div>
        </div>

        <p data-testid="wl-reorder-care-plus-footnote" className="m-0 w-full font-roboto text-[10px] leading-[16.25px] text-[#616975]">
          {footnote}
        </p>
      </div>
    </div>
  );
};

export default ReorderCarePlusPlanCard;
